import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Search, 
  Library, 
  Eye, 
  Calendar, 
  User, 
  Building,
  Play,
  FileText,
  Grid3X3,
  List,
  Image,
  ArrowUpDown,
  ArrowLeft,
  Zap,
  Folder,
  Globe
} from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserLibrary() {
  const { token } = useParams();
  const [presentations, setPresentations] = useState([]);
  const [myPresentations, setMyPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [departments, setDepartments] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchUserByToken();
    fetchPresentations();
    fetchDepartments();
  }, [token]);

  const fetchUserByToken = async () => {
    try {
      const response = await axios.get(`${API}/users/by-token/${token}`);
      setCurrentUser(response.data);
      // Fetch user's own presentations
      if (response.data?.id) {
        fetchMyPresentations(response.data.id);
      }
    } catch (error) {
      console.error("Failed to fetch user:", error);
    }
  };

  const fetchMyPresentations = async (userId) => {
    try {
      const response = await axios.get(`${API}/library/presentations?user_id=${userId}`);
      setMyPresentations(response.data.presentations || []);
    } catch (error) {
      console.error("Failed to fetch my presentations:", error);
    }
  };

  const fetchPresentations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/library/presentations`);
      setPresentations(response.data.presentations || []);
    } catch (error) {
      console.error("Failed to fetch presentations:", error);
      toast.error("Failed to load library");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await axios.get(`${API}/analytics/reports`);
      setDepartments(response.data.departments || []);
    } catch (error) {
      console.error("Failed to fetch departments:", error);
    }
  };

  const trackView = async (sessionId) => {
    try {
      await axios.post(`${API}/library/presentations/${sessionId}/view`);
    } catch (error) {
      console.error("Failed to track view:", error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Get the active presentation list
  const activePresentations = activeTab === "my" ? myPresentations : presentations;

  // Filter by search query and department
  let filteredPresentations = activePresentations;
  
  if (searchQuery) {
    filteredPresentations = filteredPresentations.filter(p =>
      p.topic_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.concept_overview?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }
  
  if (selectedDepartment !== "all") {
    filteredPresentations = filteredPresentations.filter(p => p.department === selectedDepartment);
  }

  // Sort presentations
  const sortedPresentations = [...filteredPresentations].sort((a, b) => {
    if (sortBy === "newest") {
      return new Date(b.created_at) - new Date(a.created_at);
    } else if (sortBy === "oldest") {
      return new Date(a.created_at) - new Date(b.created_at);
    } else if (sortBy === "most_viewed") {
      return (b.view_count || 0) - (a.view_count || 0);
    }
    return 0;
  });

  // Group by month
  const groupedByMonth = sortedPresentations.reduce((acc, p) => {
    const month = p.month || "Unknown";
    if (!acc[month]) acc[month] = [];
    acc[month].push(p);
    return acc;
  }, {});

  const sortedMonths = Object.keys(groupedByMonth).sort((a, b) => b.localeCompare(a));

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to={`/u/${token}`}>
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>EVOLVE</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              <Library className="inline w-7 h-7 mr-2 text-primary" />
              Presentation Library
            </h1>
            <p className="text-slate-600 mt-1">
              {activeTab === "my" 
                ? `Your ${myPresentations.length} presentations` 
                : `Learn from ${presentations.length} presentations across the organization`
              }
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={viewMode === "grid" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("grid")}
              className="rounded-full"
            >
              <Grid3X3 className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === "list" ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode("list")}
              className="rounded-full"
            >
              <List className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Tabs for My Library vs All Library */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-6">
          <TabsList className="grid w-full max-w-md grid-cols-2 bg-slate-100 rounded-full p-1">
            <TabsTrigger 
              value="all" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-2"
            >
              <Globe className="w-4 h-4" />
              All Library ({presentations.length})
            </TabsTrigger>
            <TabsTrigger 
              value="my" 
              className="rounded-full data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-2"
            >
              <Folder className="w-4 h-4" />
              My Library ({myPresentations.length})
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* Filters Row */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search Bar */}
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by name, topic, or presenter..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-5 rounded-full border-2 bg-white focus:border-primary"
            />
          </div>
          
          {/* Department Filter */}
          <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
            <SelectTrigger className="w-[180px] rounded-full bg-white">
              <Building className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Departments</SelectItem>
              {departments.map(dept => (
                <SelectItem key={dept} value={dept}>{dept}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Sort */}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] rounded-full bg-white">
              <ArrowUpDown className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
              <SelectItem value="most_viewed">Most Viewed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Loading */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : sortedPresentations.length === 0 ? (
          <div className="text-center py-20">
            <Library className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900">No presentations found</h3>
            <p className="text-slate-600 mt-2">
              {searchQuery ? "Try a different search term" : "Presentations will appear here once completed"}
            </p>
          </div>
        ) : (
          /* Gallery by Month */
          <div className="space-y-8">
            {sortedMonths.map(month => (
              <div key={month}>
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-slate-900">
                    {new Date(month + "-01").toLocaleDateString([], { month: 'long', year: 'numeric' })}
                  </h2>
                  <span className="text-sm text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                    {groupedByMonth[month].length} presentations
                  </span>
                </div>
                
                {viewMode === "grid" ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {groupedByMonth[month].map(presentation => (
                      <Link
                        key={presentation.id}
                        to={`/u/${token}/library/${presentation.id}`}
                        onClick={() => trackView(presentation.id)}
                      >
                        <Card className="group overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer border-0 shadow-lg bg-white">
                          {/* Thumbnail */}
                          <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 relative overflow-hidden">
                            {presentation.first_slide_url ? (
                              <img
                                src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                                alt={presentation.topic_name}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <Image className="w-12 h-12 text-slate-300" />
                              </div>
                            )}
                            
                            {/* Play overlay */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                              <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity transform scale-75 group-hover:scale-100">
                                <Play className="w-6 h-6 text-primary ml-1" />
                              </div>
                            </div>
                            
                            {/* View count badge */}
                            <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              {presentation.view_count || 0}
                            </div>
                            
                            {/* Slide count badge */}
                            {presentation.slide_count > 0 && (
                              <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                                <FileText className="w-3 h-3" />
                                {presentation.slide_count} slides
                              </div>
                            )}
                          </div>
                          
                          {/* Info */}
                          <CardContent className="p-4">
                            <h3 className="font-semibold text-slate-900 line-clamp-1 group-hover:text-primary transition-colors">
                              {presentation.topic_name}
                            </h3>
                            <p className="text-sm text-slate-600 line-clamp-2 mt-1 h-10">
                              {presentation.concept_overview}
                            </p>
                            <div className="flex items-center gap-2 mt-3 text-xs text-slate-500">
                              <User className="w-3 h-3" />
                              <span className="truncate">{presentation.user_name}</span>
                              {presentation.department && (
                                <>
                                  <span>•</span>
                                  <Building className="w-3 h-3" />
                                  <span className="truncate">{presentation.department}</span>
                                </>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  /* List View */
                  <div className="space-y-3">
                    {groupedByMonth[month].map(presentation => (
                      <Link
                        key={presentation.id}
                        to={`/u/${token}/library/${presentation.id}`}
                        onClick={() => trackView(presentation.id)}
                      >
                        <Card className="group hover:shadow-lg transition-all cursor-pointer border-0 shadow-md bg-white">
                          <CardContent className="p-4 flex items-center gap-4">
                            {/* Thumbnail */}
                            <div className="w-32 h-20 bg-slate-100 rounded-lg overflow-hidden flex-shrink-0">
                              {presentation.first_slide_url ? (
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}${presentation.first_slide_url}`}
                                  alt={presentation.topic_name}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Image className="w-8 h-8 text-slate-300" />
                                </div>
                              )}
                            </div>
                            
                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-slate-900 group-hover:text-primary transition-colors">
                                {presentation.topic_name}
                              </h3>
                              <p className="text-sm text-slate-600 line-clamp-1 mt-1">
                                {presentation.concept_overview}
                              </p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {presentation.user_name}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Building className="w-3 h-3" />
                                  {presentation.department || 'N/A'}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {formatDate(presentation.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  {presentation.view_count || 0} views
                                </span>
                              </div>
                            </div>
                            
                            {/* Arrow */}
                            <Play className="w-5 h-5 text-slate-400 group-hover:text-primary transition-colors" />
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
